import { ActivityItem, ActivityOutcome, ActivityType } from "../types/Activity";

/**
 * How an activity (call, meeting, note…) reads anywhere it is shown: its
 * moment, its follow-up state, its icon and labels, and a link for its
 * contact. Dates prefer the BE's ISO twins — the plain fields are formatted
 * in the server's time zone and read shifted east of it.
 */

const DAY = 86400000;

const parse = (value: string | null | undefined): Date | null => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

/** When the activity happened (or is scheduled). */
export const activityWhen = (item: Pick<ActivityItem, "activityDate" | "activityDateIso">): Date | null =>
    parse(item.activityDateIso) ?? parse(item.activityDate);

/** The follow-up day, or null when none is set. */
export const followUpWhen = (item: Pick<ActivityItem, "nextFollowUpDate" | "nextFollowUpDateIso">): Date | null =>
    parse(item.nextFollowUpDateIso) ?? parse(item.nextFollowUpDate);

/** "2026-10-12" for a date input — the follow-up's calendar day. */
export const followUpDayKey = (item: Pick<ActivityItem, "nextFollowUpDate" | "nextFollowUpDateIso">): string => {
    if (item.nextFollowUpDateIso) return item.nextFollowUpDateIso.substring(0, 10);
    const date = followUpWhen(item);
    if (!date) return "";
    const pad = (value: number) => String(value).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const startOfDay = (date: Date): number => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

/** Whole days from today to the follow-up; negative once it has passed. */
export const followUpDays = (item: Pick<ActivityItem, "nextFollowUpDate" | "nextFollowUpDateIso">): number | null => {
    const date = followUpWhen(item);
    if (!date) return null;
    // A day key stored as UTC midnight is that calendar day, wherever the reader is.
    const day = item.nextFollowUpDateIso
        ? new Date(`${item.nextFollowUpDateIso.substring(0, 10)}T00:00:00`)
        : date;
    return Math.round((startOfDay(day) - startOfDay(new Date())) / DAY);
};

export type FollowUpState = "overdue" | "today" | "upcoming" | "done";

export const followUpState = (item: ActivityItem): FollowUpState | null => {
    if (!followUpWhen(item)) return null;
    if (item.isFollowUpCompleted) return "done";
    const days = followUpDays(item) ?? 0;
    if (days < 0) return "overdue";
    return days === 0 ? "today" : "upcoming";
};

/** "Follow up today", "Follow-up 3 days late", "Follow up in 2 days", "Followed up". */
export const followUpLabel = (item: ActivityItem): string => {
    const days = followUpDays(item) ?? 0;
    switch (followUpState(item)) {
        case "overdue": return `Follow-up ${-days} day${days === -1 ? "" : "s"} late`;
        case "today": return "Follow up today";
        case "upcoming": return days === 1 ? "Follow up tomorrow" : `Follow up in ${days} days`;
        case "done": return "Followed up";
        default: return "";
    }
};

export const ACTIVITY_ICONS: Record<string, string> = {
    [ActivityType.Call]: "bi-telephone",
    [ActivityType.Email]: "bi-envelope",
    [ActivityType.Meeting]: "bi-people",
    [ActivityType.Note]: "bi-sticky",
    [ActivityType.Task]: "bi-check2-square",
    [ActivityType.WhatsApp]: "bi-whatsapp",
    [ActivityType.LinkedIn]: "bi-linkedin",
    [ActivityType.ConnectionRequest]: "bi-person-plus",
    [ActivityType.InMail]: "bi-send",
    [ActivityType.ProfileView]: "bi-eye",
};

export const activityIcon = (type: string): string => ACTIVITY_ICONS[type] ?? "bi-circle";

const capitalise = (text: string): string => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

/** "Call", "WhatsApp", "LinkedIn", "Connection request", "InMail". */
export const activityTypeLabel = (type: string): string => {
    switch (type) {
        case ActivityType.WhatsApp: return "WhatsApp";
        case ActivityType.LinkedIn: return "LinkedIn";
        case ActivityType.InMail: return "InMail";
        default: return capitalise(type);
    }
};

export const outcomeLabel = (outcome: ActivityOutcome | string | null | undefined): string => (outcome ? capitalise(outcome) : "");

/** Good outcomes read green, missed ones amber. */
export const outcomeTone = (outcome: ActivityOutcome | string | null | undefined): "success" | "warning" | "neutral" => {
    switch (outcome) {
        case ActivityOutcome.Connected:
        case ActivityOutcome.Successful: return "success";
        case ActivityOutcome.NoAnswer:
        case ActivityOutcome.Voicemail:
        case ActivityOutcome.FollowUpNeeded: return "warning";
        default: return "neutral";
    }
};

/** A phone / email / profile as a link the device can open: tel:, mailto: or the web. Null for a blank value. */
export const contactHref = (type: string, contactInfo: string | null | undefined): string | null => {
    const value = (contactInfo ?? "").trim();
    if (value.replace(/[\s+]/g, "").length <= 3) return null;
    if (type === ActivityType.Email || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) return `mailto:${value}`;
    if (type === ActivityType.Call || type === ActivityType.WhatsApp || /^[+\d][\d\s()-]{5,}$/.test(value)) return `tel:${value.replace(/[^\d+]/g, "")}`;
    return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};

/** A meeting link as an absolute URL. */
export const meetingHref = (link: string | null | undefined): string | null => {
    const value = (link ?? "").trim();
    if (!value) return null;
    return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};

/** "Today 14:00", "Yesterday 09:30", "Tue 11:00", "12 Oct 2025". */
export const formatActivityWhen = (date: Date | null): string => {
    if (!date) return "";
    const days = Math.round((startOfDay(date) - startOfDay(new Date())) / DAY);
    const time = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    if (days === 0) return `Today ${time}`;
    if (days === -1) return `Yesterday ${time}`;
    if (days === 1) return `Tomorrow ${time}`;
    if (days > -7 && days < 7) return `${date.toLocaleDateString("en-GB", { weekday: "short" })} ${time}`;
    const sameYear = date.getFullYear() === new Date().getFullYear();
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) });
};

/** The full moment for a tooltip. */
export const fullWhen = (date: Date | null): string =>
    date ? date.toLocaleString("en-GB", { dateStyle: "full", timeStyle: "short" }) : "";
